import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';

import PaginationLeft2Icon from '../Icons/PaginationLeft2Icon';
import PaginationRight2Icon from '../Icons/PaginationRight2Icon';
import PaginationLeftIcon from '../Icons/PaginationLeftIcon';
import PaginationRightIcon from '../Icons/PaginationRightIcon';
import useBoardPagination from './hooks/useBoardPagination';
import TrackingService from '@/services/tracking';
import { theme } from '@/styles';

// pagination 생성기
// showPages: 보여질 페이지 수
// pages: 데이터들의 전체 페이지 수
// currentPage: 현재 페이지
export const generatePagination = (
  pages: number,
  currentPage: number,
  showPages: number,
) => {
  return Array.from(
    { length: pages >= showPages ? showPages : pages },
    (_, i) => {
      if (currentPage <= 3 && pages <= showPages) {
        return i + 1;
      } else if (currentPage > 3 && pages > showPages) {
        if (currentPage <= pages - 2) {
          return currentPage - 2 + i;
        } else if (currentPage > pages - 2) {
          return pages - showPages + i + 1;
        }
      }
      return i + 1;
    },
  );
};
export type PaginationType = 'prev1' | 'prev2' | 'next1' | 'next2' | 'page';

export type PaginationProps = {
  page: number;
  handlePage?: (page: number) => void;
  totalPages: number;
  showPages?: 5 | 10;
  paginationType?: 'dynamic' | 'static';
  hasFirstArrow?: boolean;
  hasLastArrow?: boolean;
  value?: string;
  isRouterUrlChange?: boolean;
  routerUrl?: string;
  gtmName?: string;
};

/**
 * @param page 현재 페이지
 * @param handlePage 페이지 핸들링 함수
 * @param totalPages 총 페이지 수
 * @param showPages
 * - 페이지네이션으로 보여질 페이지 버튼 개수
 * - 5 Or 10 으로 타입 설정.
 * - 5일 때 paginationType="dynamic"
 * - 10일 때 paginationType="static"
 * @param hasFirstArrow 처음으로 이동 버튼 여부(<<)
 * @param hasLastArrow 마지막으로 이동 버튼 여부(>>)
 * @param paginationType 페이지네이션 타입 설정(dynamic)
 * @param value 우편번호 검색 시 필요한 파라미터(주소 검색 값 변경 여부 체크)
 * @param isRouterUrlChange 페이지 클릭시 query-string 변경할건지 여부
 * - 초기값 false
 * @param routerUrl 페이지 클릭시 query-string에 표시되는 URL
 * ex) `/my/consult` / `/shop/search`
 * @param gtmName BoardPagination이 쓰이는 컴포넌트 페이지 이름입니다.(gtm을 위해 이 값을 넘깁니다.)
 * gtmName을 optional param으로 설정하였고, gtmName을 넘기지 않으면, 빈 문자열로 받아집니다.
 * ex) 'shop_search'
 */
function BoardPagination({
  page,
  handlePage,
  totalPages,
  showPages = 5,
  hasFirstArrow = true,
  hasLastArrow = true,
  paginationType = 'dynamic',
  value = '',
  isRouterUrlChange = false,
  routerUrl,
  gtmName = '',
}: PaginationProps) {
  const {
    generatedPagination,
    handlePageRouter,
    onClickPageNumber,
    setHandleType,
  } = useBoardPagination({
    page,
    totalPages,
    handlePage,
    isRouterUrlChange,
    routerUrl,
    paginationType,
    showPages,
    value,
  });

  /**
   * @description pagination GTM 객체 만드는 함수입니다.
   * @param type page, prev1, prev2, next1, next2
   * @example
   * ```
   * <tag
   *   ...
   *   {...getPaginationGtm('page')}
   * >
   *    ...
   * </tag>
   * ```
   */
  const getPaginationGtm = useCallback(
    (type: PaginationType) => {
      let name = gtmName + '_pagination_';

      switch (type) {
        case 'next1':
          name += 'next1_button';
          break;
        case 'next2':
          name += 'next2_button';
          break;
        case 'prev1':
          name += 'prev1_button';
          break;
        case 'prev2':
          name += 'prev2_button';
          break;
        default:
          name += `${page}_page_button`;
      }

      return TrackingService.tagGTM(name);
    },
    [gtmName, page],
  );

  return (
    <BoardPaginationWrap {...TrackingService.tagGTM(gtmName)}>
      <BoardPaginationInner className="board-pagination-inner">
        <PaginationButtonGroup
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {hasFirstArrow && (
            <PaginationIconWrap
              onClick={() => handlePageRouter('first')}
              isVisible={page === 1 || totalPages < 2 ? 'hidden' : 'visible'}
              {...getPaginationGtm('prev2')}
            >
              <PaginationLeft2Icon />
            </PaginationIconWrap>
          )}
          <PaginationIconWrap
            onClick={() => {
              setHandleType('prev');
              handlePageRouter('prev');
            }}
            isVisible={page === 1 || totalPages < 2 ? 'hidden' : 'visible'}
            {...getPaginationGtm('prev1')}
          >
            <PaginationLeftIcon />
          </PaginationIconWrap>
        </PaginationButtonGroup>
        <PaginationNumberList className="pagination-number-list">
          {generatedPagination.map((_page, index, arr) => {
            return (
              <PageNumber
                key={`pagination-page-item-${index}`}
                onClick={() => {
                  onClickPageNumber(_page);
                }}
                isActive={page === arr[index]}
                page={_page}
                {...getPaginationGtm('page')}
              >
                {_page}
              </PageNumber>
            );
          })}
        </PaginationNumberList>
        <PaginationButtonGroup
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <PaginationIconWrap
            onClick={() => {
              setHandleType('next');
              handlePageRouter('next');
            }}
            isVisible={
              totalPages <= 5 || page === totalPages ? 'hidden' : 'visible'
            }
            {...getPaginationGtm('next1')}
          >
            <PaginationRightIcon />
          </PaginationIconWrap>
          {hasLastArrow && (
            <PaginationIconWrap
              onClick={() => handlePageRouter('last')}
              {...getPaginationGtm('next2')}
              isVisible={
                totalPages <= 5 || page === totalPages ? 'hidden' : 'visible'
              }
            >
              <PaginationRight2Icon />
            </PaginationIconWrap>
          )}
        </PaginationButtonGroup>
      </BoardPaginationInner>
    </BoardPaginationWrap>
  );
}

export default BoardPagination;

const BoardPaginationWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const BoardPaginationInner = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const PaginationButtonGroup = styled.div``;

const PaginationIconWrap = styled.div<{
  isVisible?: 'visible' | 'hidden';
  isActive?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: black;
  background-color: transparent;
  cursor: ${({ isActive }) => (isActive ? 'default' : 'pointer')};
  &:focus {
    outline: none;
    background-color: transparent;
  }
  &:active {
    background-color: transparent;
  }
  &:hover {
    background-color: transparent;
  }
`;

const PaginationNumberList = styled.ul`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 20px;
`;

const PageNumber = styled.div<{ isActive?: boolean; page: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 21px;
  height: 20px;
  ${({ isActive }) =>
    isActive ? theme.typography.button.md_b : theme.typography.button.md_r};
  cursor: pointer;
  position: relative;
  ${({ isActive, page }) => {
    return (
      isActive &&
      css`
        &::after {
          content: '';
          position: absolute;
          bottom: 2px;
          left: 50%;
          transform: translateX(-50%);
          width: ${String(page).length * 9}px;
          height: 1.4px;
          background-color: ${theme.colors.neutral_colors.primary};
        }
      `
    );
  }}
`;
